import {
  useWSModalCustom,
  WSAlert,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSTable,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import {
  RequirementDefinitionChangePreview,
  RequirementDefinitionUpdateHelpers
} from "@wingspanhq/payments/dist/services/requirements/requirementDefinitionUpdateHelpers";
import {
  RequirementDefinition,
  UpdateRequirementDefinitionRequest
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";

type ComparisonRow = {
  newer: string;
  older: string;
};

type ModalLabels = RequirementDefinitionChangePreview["modalLabels"];

const buildComparisonRows = (labels: ModalLabels): ComparisonRow[] => {
  const rows: ComparisonRow[] = [];

  if (labels.renewals) {
    rows.push({
      newer: labels.renewals.after,
      older: labels.renewals.before
    });
  }

  if (labels.expiration) {
    rows.push({
      newer: labels.expiration.after,
      older: labels.expiration.before
    });
  }

  if (labels.approval) {
    rows.push({
      newer: labels.approval.after,
      older: labels.approval.before
    });
  }

  return rows;
};

const buildTableData = (
  rows: ComparisonRow[]
): Array<{ data: ComparisonRow; id: string }> =>
  rows.map((row, index) => ({
    data: row,
    id: String(index)
  }));

const TABLE_COLUMNS: WSTableColumn<ComparisonRow>[] = [
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Older version" }
    },
    renderFunction: ({ data }) => <WSTableCell text={data.older} />
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Newer version" }
    },
    renderFunction: ({ data }) => <WSTableCell text={data.newer} />
  }
];

type Props = {
  requirementDefinition: RequirementDefinition;
  update: UpdateRequirementDefinitionRequest;
} & WSModalCustomProps<void>;

const ModalAboutRequirementVersions: React.FC<Props> = ({
  requirementDefinition,
  update,
  ...modalProps
}) => {
  const comparisonRows = useMemo(() => {
    // Pure client-side label diff shipped in the v1 package. It reads v1 field
    // names, so the three renamed/nested v3 fields are bridged back:
    // `reviewPolicy` → `reviewStrategy` and `transition.{strategy,expiresAt}` →
    // flat `versionTransition*`. The helper gates each label on a `'field' in
    // update` check, so the keys are only added when the v3 source is present.
    // Everything else (`expirationStrategy`, `expirationDays`,
    // `manualRenewalAllowed`) kept its v1 name on both v3 shapes.
    const v1Definition = {
      ...requirementDefinition,
      reviewStrategy: requirementDefinition.reviewPolicy
    };
    const v1Update = {
      ...update,
      ...("reviewPolicy" in update
        ? { reviewStrategy: update.reviewPolicy }
        : {}),
      ...(update.transition?.strategy !== undefined
        ? { versionTransitionStrategy: update.transition.strategy }
        : {}),
      ...(update.transition?.expiresAt !== undefined
        ? { versionTransitionExpiresAt: update.transition.expiresAt }
        : {})
    };

    const preview =
      RequirementDefinitionUpdateHelpers.previewRequirementDefinitionUpdate(
        v1Definition as never,
        v1Update as never
      );

    return buildComparisonRows(preview.modalLabels);
  }, [requirementDefinition, update]);

  const tableData = useMemo(
    () => buildTableData(comparisonRows),
    [comparisonRows]
  );

  return (
    <WSModal
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Got it",
            onClick: () => modalProps.onClose()
          }
        ]
      }}
      size="M"
      title="About versions"
      {...modalProps}
    >
      <WSList gap="XL">
        <WSAlert
          icon="info-circle"
          size="M"
          theme="info"
          title="About versions and affected contractors"
        >
          Contractors may be disadvantaged (such as facing higher compliance
          burden) when they are kept on an older version of the requirement if a
          newer version offer improvements. See examples below.
        </WSAlert>

        <WSList gap="M">
          <WSTable<ComparisonRow>
            columns={TABLE_COLUMNS}
            tableData={tableData}
          />

          <WSInfoBox title="Pro-tip">
            Review the impact comparison of your settings to confirm it is your
            intended outcome.
          </WSInfoBox>
        </WSList>
      </WSList>
    </WSModal>
  );
};

export const useAboutRequirementVersionsModal = () =>
  useWSModalCustom(ModalAboutRequirementVersions);
